/**
 * Branded ID primitives — make a `WorkOrderId` structurally distinct from a
 * `StopId` even though both are strings at runtime. Mixing them at a call site
 * becomes a compile error, which is the entire point of the contract layer.
 *
 * Construction is funneled through the `as*` helpers; they're the only place
 * the `as` cast is permitted. Adapters at the RTKQ boundary call them.
 */

/* eslint-disable no-underscore-dangle -- brand symbols, not fields */
declare const __routeIdBrand: unique symbol
declare const __workOrderIdBrand: unique symbol
declare const __stopIdBrand: unique symbol
declare const __masterStopIdBrand: unique symbol
declare const __cseIdBrand: unique symbol
declare const __routeSlotIdBrand: unique symbol
declare const __serviceDateBrand: unique symbol
/* eslint-enable no-underscore-dangle */

export type RouteId = string & { readonly [__routeIdBrand]: 'RouteId' }
export type WorkOrderId = string & { readonly [__workOrderIdBrand]: 'WorkOrderId' }
export type StopId = string & { readonly [__stopIdBrand]: 'StopId' }
export type MasterStopId = string & { readonly [__masterStopIdBrand]: 'MasterStopId' }
export type CseId = string & { readonly [__cseIdBrand]: 'CseId' }
export type RouteSlotId = string & { readonly [__routeSlotIdBrand]: 'RouteSlotId' }

/** ISO `YYYY-MM-DD` (BE-format date). The brand keeps it from being mixed with display strings. */
export type ServiceDate = string & { readonly [__serviceDateBrand]: 'ServiceDate' }

/**
 * Lowercase day-of-week literal union, matching the values BE sends/expects
 * (`master/stops?dayOfWeek=monday`). Used as a discriminator on master ops.
 */
export type DayOfWeek = 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday'

export const DAYS_OF_WEEK: ReadonlyArray<DayOfWeek> = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
] as const

export const asRouteId = (s: string): RouteId => s as RouteId
export const asWorkOrderId = (s: string): WorkOrderId => s as WorkOrderId
export const asStopId = (s: string): StopId => s as StopId
export const asMasterStopId = (s: string): MasterStopId => s as MasterStopId
export const asCseId = (s: string): CseId => s as CseId
export const asRouteSlotId = (s: string): RouteSlotId => s as RouteSlotId
export const asServiceDate = (s: string): ServiceDate => s as ServiceDate

/**
 * Type guard for narrowing an arbitrary string to `DayOfWeek`. Use at adapter
 * boundaries — the utility code itself only accepts the branded literal union.
 */
export const isDayOfWeek = (s: string): s is DayOfWeek => (DAYS_OF_WEEK as ReadonlyArray<string>).includes(s)
